/**
 * Gift Plate Preview Component
 * 
 * Renders a realistic metal plate preview for gift plates.
 * Text is editable directly on the plate.
 * Wide format with dedicated vertical date column on the right.
 */

import { useState, useRef, useEffect, useMemo } from 'react'

interface GiftPlatePreviewProps {
  text: string
  onTextChange: (text: string) => void
  backgroundColor: string
  backgroundImage?: string | null
  textColor: string
  frameColor: string
  showDate: boolean
  date?: { day: number; month: number; year: number }
  datePosition?: 'left' | 'right'
  holesEnabled: boolean
  holePositions: string[]
}

const GiftPlatePreview = ({
  text,
  onTextChange,
  backgroundColor,
  backgroundImage,
  textColor,
  frameColor,
  showDate,
  date,
  datePosition = 'right',
  holesEnabled,
  holePositions,
}: GiftPlatePreviewProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState(text)
  const inputRef = useRef<HTMLInputElement>(null)
  const textRef = useRef<HTMLDivElement>(null)

  // Plate dimensions - WIDE format to accommodate text + date column
  // Responsive sizing: smaller on mobile, full size on desktop
  const [plateDimensions, setPlateDimensions] = useState({ width: 750, height: 200 })

  useEffect(() => {
    const updateDimensions = () => {
      const screenWidth = window.innerWidth
      const isMobile = screenWidth < 640 // sm breakpoint
      const isTablet = screenWidth >= 640 && screenWidth < 1024
      
      if (isMobile) {
        // Mobile: Use 90% of screen width with padding, ensure no scroll
        const containerPadding = 32 // 16px on each side
        const maxWidth = screenWidth - containerPadding
        const width = Math.min(750, maxWidth)
        const height = Math.round(width * 0.267)
        setPlateDimensions({ width, height })
      } else if (isTablet) {
        // Tablet: Use 85% of screen width
        const containerPadding = 48
        const maxWidth = Math.min(750, screenWidth - containerPadding)
        const width = maxWidth
        const height = Math.round(width * 0.267)
        setPlateDimensions({ width, height })
      } else {
        // Desktop: Full size
        setPlateDimensions({ width: 750, height: 200 })
      }
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  const PLATE_WIDTH = plateDimensions.width
  const PLATE_HEIGHT = plateDimensions.height
  // Date column width: ensure minimum 80px, scale with plate width
  const DATE_COLUMN_WIDTH = showDate ? Math.max(80, Math.round(PLATE_WIDTH * 0.16)) : 0
  const TEXT_AREA_PADDING = PLATE_WIDTH < 400 ? Math.round(PLATE_WIDTH * 0.04) : 30
  const TEXT_AREA_MAX_WIDTH = PLATE_WIDTH - DATE_COLUMN_WIDTH - (TEXT_AREA_PADDING * 2)
  
  // Convert text to UPPERCASE (letters only, preserve symbols/emojis)
  const convertToUppercase = (str: string): string => {
    return str.split('').map(char => {
      // Check if character is a letter (A-Z or a-z)
      if (/[a-zA-Z]/.test(char)) {
        return char.toUpperCase()
      }
      // Preserve symbols, emojis, and other characters
      return char
    }).join('')
  }
  
  // Apply uppercase conversion to text for display
  const displayText = text ? convertToUppercase(text) : ''

  useEffect(() => {
    setEditText(text)
  }, [text])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Format date for display (DD / MM / YYYY vertical with separators)
  const formatDate = (): string[] => {
    if (!date) return []
    const day = String(date.day).padStart(2, '0')
    const month = String(date.month).padStart(2, '0')
    const year = String(date.year)
    return [day, '—', month, '—', year]
  }

  // Calculate text font size based on text length and available width
  // Text must stay on ONE horizontal line, never wrap
  // Responsive base size that scales with plate width
  const textFontSize = useMemo(() => {
    if (!text) {
      // Responsive default size based on plate width
      return PLATE_WIDTH < 400 ? 32 : PLATE_WIDTH < 600 ? 48 : 64
    }
    
    // Base size scales with plate width
    const baseSize = PLATE_WIDTH < 400 ? 32 : PLATE_WIDTH < 600 ? 48 : 64
    const minSize = PLATE_WIDTH < 400 ? 20 : PLATE_WIDTH < 600 ? 28 : 42
    const maxSize = PLATE_WIDTH < 400 ? 40 : PLATE_WIDTH < 600 ? 60 : 80
    
    // Estimate text width with increased letter spacing
    // Average character width is approximately 0.7 × font size (wider spacing)
    // Emojis and symbols may be wider, so we use a conservative estimate
    const letterSpacing = 0.15 // 15% additional spacing between letters
    const estimatedWidth = text.length * (baseSize * 0.7 * (1 + letterSpacing))
    
    if (estimatedWidth > TEXT_AREA_MAX_WIDTH) {
      // Scale down proportionally to fit on one line
      const scale = TEXT_AREA_MAX_WIDTH / estimatedWidth
      const newSize = Math.max(minSize, baseSize * scale)
      return Math.floor(newSize)
    }
    
    return Math.min(maxSize, baseSize)
  }, [text, TEXT_AREA_MAX_WIDTH, PLATE_WIDTH])
  
  // Letter spacing (tracking) - increased for premium look
  const letterSpacing = useMemo(() => {
    // Base spacing increases with font size
    const baseSpacing = textFontSize * 0.12 // 12% of font size
    return `${baseSpacing}px`
  }, [textFontSize])

  // Check if text is at layout limit
  const isTextAtLimit = useMemo(() => {
    if (!text) return false
    const estimatedWidth = text.length * (textFontSize * 0.65)
    return estimatedWidth >= TEXT_AREA_MAX_WIDTH * 0.9 // 90% of max width
  }, [text, textFontSize, TEXT_AREA_MAX_WIDTH])

  // Handle text edit
  const handleTextClick = () => {
    setIsEditing(true)
    setEditText(text) // Keep original for editing, will convert on blur
  }

  const handleTextBlur = () => {
    setIsEditing(false)
    if (editText.length <= 10) {
      // Convert to uppercase before saving
      const uppercaseText = convertToUppercase(editText)
      onTextChange(uppercaseText)
    } else {
      setEditText(text) // Revert if too long
    }
  }

  const handleTextKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTextBlur()
    } else if (e.key === 'Escape') {
      setEditText(text)
      setIsEditing(false)
    }
  }

  // Check if hole position is active
  const hasHole = (position: string) => {
    return holesEnabled && holePositions.includes(position)
  }

  // Calculate hole positions (adjusted for wider plate)
  const getHoleStyle = (position: string) => {
    const offset = 15
    
    switch (position) {
      case 'top-left':
        return { top: `${offset}px`, left: `${offset}px` }
      case 'top-right':
        return { top: `${offset}px`, right: `${offset}px` }
      case 'top-center':
        return { top: `${offset}px`, left: '50%', transform: 'translateX(-50%)' }
      case 'bottom-left':
        return { bottom: `${offset}px`, left: `${offset}px` }
      case 'bottom-right':
        return { bottom: `${offset}px`, right: `${offset}px` }
      case 'bottom-center':
        return { bottom: `${offset}px`, left: '50%', transform: 'translateX(-50%)' }
      case 'center-left':
        return { top: '50%', left: `${offset}px`, transform: 'translateY(-50%)' }
      case 'center-right':
        return { top: '50%', right: `${offset}px`, transform: 'translateY(-50%)' }
      case 'center':
        return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
      default:
        return {}
    }
  }

  return (
    <div className="bg-dark-900 rounded-2xl p-6 border border-gray-800 w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-300 mb-2">Vorschau</h3>
        <p className="text-xs text-gray-500">Klicken Sie auf den Text zum Bearbeiten</p>
        {isTextAtLimit && (
          <p className="text-xs text-yellow-400 mt-1 flex items-center gap-1">
            <span>⚠️</span>
            <span>Text erreicht Layout-Grenze</span>
          </p>
        )}
      </div>

      <div className="flex justify-center items-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-2 sm:p-4 md:p-8 shadow-inner w-full overflow-hidden">
        <div
          className="relative bg-white border-4 rounded-lg shadow-2xl overflow-hidden"
          style={{
            width: `${PLATE_WIDTH}px`,
            height: `${PLATE_HEIGHT}px`,
            maxWidth: '100%',
            borderColor: frameColor,
            background: backgroundImage || backgroundColor,
            backgroundSize: backgroundImage && backgroundImage.startsWith('url') ? 'cover' : 'auto',
            backgroundPosition: 'center',
            boxShadow: `
              0 4px 6px rgba(0, 0, 0, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.2),
              inset 0 -1px 0 rgba(0, 0, 0, 0.1)
            `,
          }}
        >
          {/* Emboss effect overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                linear-gradient(225deg, rgba(0, 0, 0, 0.05) 0%, transparent 50%)
              `,
            }}
          />

          {/* Visual separator between text area and date column */}
          {showDate && (
            <div
              className="absolute top-0 bottom-0 pointer-events-none"
              style={{
                [datePosition === 'right' ? 'left' : 'right']: `${datePosition === 'right' ? PLATE_WIDTH - DATE_COLUMN_WIDTH : DATE_COLUMN_WIDTH}px`,
                width: '2px',
                background: `linear-gradient(to bottom, transparent 0%, ${frameColor}40 20%, ${frameColor}40 80%, transparent 100%)`,
              }}
            />
          )}

          {/* Holes */}
          {hasHole('top-left') && (
            <div
              className="absolute w-3 h-3 rounded-full bg-gray-800 border-2 border-gray-600"
              style={getHoleStyle('top-left')}
            />
          )}
          {hasHole('top-right') && (
            <div
              className="absolute w-3 h-3 rounded-full bg-gray-800 border-2 border-gray-600"
              style={getHoleStyle('top-right')}
            />
          )}
          {hasHole('top-center') && (
            <div
              className="absolute w-3 h-3 rounded-full bg-gray-800 border-2 border-gray-600"
              style={getHoleStyle('top-center')}
            />
          )}
          {hasHole('bottom-left') && (
            <div
              className="absolute w-3 h-3 rounded-full bg-gray-800 border-2 border-gray-600"
              style={getHoleStyle('bottom-left')}
            />
          )}
          {hasHole('bottom-right') && (
            <div
              className="absolute w-3 h-3 rounded-full bg-gray-800 border-2 border-gray-600"
              style={getHoleStyle('bottom-right')}
            />
          )}
          {hasHole('bottom-center') && (
            <div
              className="absolute w-3 h-3 rounded-full bg-gray-800 border-2 border-gray-600"
              style={getHoleStyle('bottom-center')}
            />
          )}
          {hasHole('center-left') && (
            <div
              className="absolute w-3 h-3 rounded-full bg-gray-800 border-2 border-gray-600"
              style={getHoleStyle('center-left')}
            />
          )}
          {hasHole('center-right') && (
            <div
              className="absolute w-3 h-3 rounded-full bg-gray-800 border-2 border-gray-600"
              style={getHoleStyle('center-right')}
            />
          )}
          {hasHole('center') && (
            <div
              className="absolute w-3 h-3 rounded-full bg-gray-800 border-2 border-gray-600"
              style={getHoleStyle('center')}
            />
          )}

          {/* Vertical Date Column - FULL HEIGHT on LEFT or RIGHT side */}
          {showDate && date && (
            <div
              className="absolute top-0 bottom-0 flex flex-col items-center justify-center font-black"
              style={{ 
                [datePosition === 'right' ? 'right' : 'left']: '0',
                width: `${DATE_COLUMN_WIDTH}px`,
                height: '100%',
                color: textColor,
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
                zIndex: 10,
              }}
            >
              {formatDate().map((item: string, i: number) => {
                const isSeparator = item === '—'
                const dateFontSize = PLATE_WIDTH < 400 ? '18px' : PLATE_WIDTH < 600 ? '22px' : '28px'
                const separatorFontSize = PLATE_WIDTH < 400 ? '14px' : PLATE_WIDTH < 600 ? '16px' : '20px'
                
                return (
                  <div 
                    key={i} 
                    className={isSeparator ? 'text-gray-400' : ''}
                    style={{
                      fontSize: isSeparator ? separatorFontSize : dateFontSize,
                      fontWeight: isSeparator ? 'normal' : '900',
                      lineHeight: isSeparator ? '0.8' : '1.2',
                      margin: isSeparator ? '2px 0' : '0',
                    }}
                  >
                    {item}
                  </div>
                )
              })}
            </div>
          )}

          {/* Main Text Area - ONE horizontal line, spans full available width */}
          <div
            className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center"
            style={{
              left: showDate && datePosition === 'left' ? `${DATE_COLUMN_WIDTH + TEXT_AREA_PADDING}px` : `${TEXT_AREA_PADDING}px`,
              right: showDate && datePosition === 'right' ? `${DATE_COLUMN_WIDTH + TEXT_AREA_PADDING}px` : `${TEXT_AREA_PADDING}px`,
              maxWidth: `${TEXT_AREA_MAX_WIDTH}px`,
              color: textColor,
            }}
          >
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                value={editText}
                onChange={(e) => {
                  const value = e.target.value
                  if (value.length <= 10) {
                    // Convert to uppercase while typing (letters only)
                    const uppercaseValue = value.split('').map(char => {
                      if (/[a-zA-Z]/.test(char)) {
                        return char.toUpperCase()
                      }
                      return char
                    }).join('')
                    setEditText(uppercaseValue)
                  }
                }}
                onBlur={handleTextBlur}
                onKeyDown={handleTextKeyDown}
                className="bg-transparent border-2 border-dashed border-current outline-none font-black text-center w-full"
                style={{ 
                  color: textColor,
                  fontSize: `${textFontSize}px`,
                  letterSpacing: letterSpacing,
                }}
                maxLength={10}
              />
            ) : (
              <div
                ref={textRef}
                onClick={handleTextClick}
                className="font-black whitespace-nowrap cursor-text hover:opacity-80 transition-opacity text-center relative"
                style={{
                  fontSize: `${textFontSize}px`,
                  letterSpacing: letterSpacing,
                  opacity: isTextAtLimit ? 0.95 : 1,
                  // Embossed/Raised text effect
                  textShadow: `
                    /* Bottom shadow for depth */
                    0 2px 0 rgba(0, 0, 0, 0.3),
                    0 3px 2px rgba(0, 0, 0, 0.25),
                    0 4px 4px rgba(0, 0, 0, 0.2),
                    /* Top highlight for raised effect */
                    0 -1px 0 rgba(255, 255, 255, 0.4),
                    0 -2px 1px rgba(255, 255, 255, 0.3),
                    /* Additional depth */
                    0 1px 1px rgba(0, 0, 0, 0.4)
                  `,
                  filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.3))',
                }}
              >
                {displayText || 'Klicken zum Bearbeiten'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default GiftPlatePreview
